/**
 * <announcement-bar-countdown> custom element.
 *
 * Renders a fixed-duration countdown that starts from the merchant-configured
 * H/M/S each time the page loads, ticks down at 1Hz, and AUTO-LOOPS back to
 * the configured duration when it hits zero — so visitors always see a count
 * close to the configured value ("always 02h:17min remaining" pattern).
 *
 * No persistence; each page load is its own fresh countdown. Designed for
 * "artificial urgency" — pure marketing nudge, not tied to any real deadline.
 *
 * Date mode (`data-end-date` set): counts down to a fixed end date instead,
 * adds a days unit while more than 24h remain, and stays at 00:00 once the
 * date has passed.
 *
 * Server-rendered initial values (in Liquid) are overwritten on hydration so
 * cached HTML can't show stale counts.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('announcement-bar-countdown')) return;

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  class AnnouncementBarCountdown extends HTMLElement {
    connectedCallback() {
      this.endTime = this.dataset.endDate ? new Date(this.dataset.endDate).getTime() : NaN;
      this.isDateMode = !Number.isNaN(this.endTime);
      this.isInline = this.classList.contains('acb--inline');

      const h = parseInt(this.dataset.displayHours, 10) || 0;
      const m = parseInt(this.dataset.displayMinutes, 10) || 0;
      const s = parseInt(this.dataset.displaySeconds, 10) || 0;
      this.initialSeconds = h * 3600 + m * 60 + s;

      // Safety guard — at least 1 minute. Without this, a misconfigured
      // 0/0/0 would divide-by-zero in the modulo math below.
      if (this.initialSeconds <= 0) this.initialSeconds = 60;

      this.startTimestamp = Date.now();

      this.hoursEl = this.querySelector('[data-acb-hours]');
      this.minutesEl = this.querySelector('[data-acb-minutes]');
      this.secondsEl = this.querySelector('[data-acb-seconds]');
      this.daysEl = this.querySelector('[data-acb-days]');
      this.daysUnitEl = this.querySelector('[data-acb-days-unit]');
      this.daysSepEl = this.querySelector('[data-acb-days-sep]');

      // Countdown hidden via section settings — nothing to tick.
      if (!this.hoursEl) return;

      // First tick immediately so the server-rendered values are replaced
      // with the actual ticking values before the visitor's eyes settle.
      this.tick();

      // Already past the end date — stays at 00:00, no need to tick.
      if (this.isDateMode && this.endTime <= Date.now()) return;

      // 1Hz updates. Looping is handled inside `tick()` via modulo math.
      this.timer = window.setInterval(() => this.tick(), 1000);

      // Pause-and-resync on visibility loss/restore. Saves CPU while the
      // tab is backgrounded; instant resync when the visitor switches back
      // (otherwise setInterval throttling can show a drifted count for a
      // few seconds after focus returns).
      this._onVisibility = () => {
        if (document.visibilityState === 'visible') this.tick();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
    }

    tick() {
      if (this.isDateMode) return this.tickToDate();

      // Time spent on this page since the component mounted.
      const elapsed = Math.floor((Date.now() - this.startTimestamp) / 1000);

      // Modulo arithmetic makes the loop free: when `elapsed` reaches
      // `initialSeconds`, position resets to 0 and remaining resets to
      // `initialSeconds`. So the countdown ticks down → hits 1 → snaps
      // back to the configured H/M/S and continues. No state flag needed.
      const positionInCycle = elapsed % this.initialSeconds;
      const remaining = this.initialSeconds - positionInCycle;

      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;

      if (this.hoursEl) this.hoursEl.textContent = pad2(h);
      if (this.minutesEl) this.minutesEl.textContent = pad2(m);
      if (this.secondsEl) this.secondsEl.textContent = pad2(s);
    }

    tickToDate() {
      const remaining = Math.max(0, Math.floor((this.endTime - Date.now()) / 1000));
      const d = Math.floor(remaining / 86400);
      const h = Math.floor((remaining % 86400) / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;

      if (this.daysEl) {
        // Inline reads "2d 05:12" so the day count isn't zero-padded there.
        this.daysEl.textContent = this.isInline ? String(d) : pad2(d);
        this.daysUnitEl.hidden = d === 0;
        this.daysSepEl.hidden = d === 0;
      }
      if (this.hoursEl) this.hoursEl.textContent = pad2(h);
      if (this.minutesEl) this.minutesEl.textContent = pad2(m);
      if (this.secondsEl) this.secondsEl.textContent = pad2(s);

      // Expired — freeze at 00:00.
      if (remaining === 0 && this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  customElements.define('announcement-bar-countdown', AnnouncementBarCountdown);
})();
